// Screen 12 · Scan.
// Answers: what is this thing, and what do I do with it?
// One code format on every card, printed shop item and bin. The result decides the action;
// there is no mode to choose first. A typed-code field always sits under the camera.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCrumb } from '../frame/Frame';
import { fetchRows, insertRow, toast, useRows, type Row } from '../lib/api';
import { qty, shortDate } from '../lib/format';
import { freeReceipt, kanbanRaise, kanbanReturn, receive, useInventory } from '../lib/inventory';
import { useSession } from '../lib/session';
import { Btn, Empty, Head, Ln, Panel } from '../ui/kit';
import { cap, useInventoryNames, useOrderStateNames } from './inventory/names';
import { SopLink } from './Refs';

export function Scan() {
  useCrumb([{ label: 'Scan' }]);
  const [code, setCode] = useState('');
  const [typed, setTyped] = useState('');
  const [camera, setCamera] = useState<'off' | 'on' | 'unsupported' | 'blocked'>('off');
  const video = useRef<HTMLVideoElement>(null);

  // read codes from the camera where the browser can
  useEffect(() => {
    if (camera !== 'on') return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamera('unsupported');
      return;
    }
    const Detector = (window as any).BarcodeDetector;
    let stream: MediaStream | null = null;
    let timer = 0;
    let live = true;
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (!live || !video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        // the browser's own reader where it has one; where it has none (iPhone, iPad), the
        // QR code is read out of the camera picture instead
        const detector = Detector ? new Detector({ formats: ['qr_code', 'code_128', 'code_39'] }) : null;
        const readQR = detector ? null : (await import('jsqr')).default;
        const canvas = document.createElement('canvas');
        let reading = false;
        timer = window.setInterval(async () => {
          const v = video.current;
          if (!v || v.readyState < 2 || reading) return;
          reading = true;
          try {
            let value: string | null = null;
            if (detector) {
              const found = await detector.detect(v).catch(() => []);
              value = found[0]?.rawValue ? String(found[0].rawValue) : null;
            } else if (readQR && v.videoWidth > 0) {
              const scale = Math.min(1, 720 / Math.max(v.videoWidth, v.videoHeight));
              canvas.width = Math.round(v.videoWidth * scale);
              canvas.height = Math.round(v.videoHeight * scale);
              const ctx = canvas.getContext('2d', { willReadFrequently: true });
              if (ctx) {
                ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
                const picture = ctx.getImageData(0, 0, canvas.width, canvas.height);
                value = readQR(picture.data, picture.width, picture.height, { inversionAttempts: 'dontInvert' })?.data || null;
              }
            }
            if (value && live) {
              setCode(value.trim());
              setCamera('off');
            }
          } finally {
            reading = false;
          }
        }, 300);
      } catch {
        setCamera('blocked');
      }
    })();
    return () => {
      live = false;
      window.clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [camera]);

  return (
    <div className="stack" style={{ maxWidth: 560, margin: '0 auto' }}>
      <Head title="Scan" />
      <div className="pic" style={{ aspectRatio: '4 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {camera === 'on' ? (
          <video ref={video} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div className="blank" style={{ aspectRatio: 'auto', flexDirection: 'column', gap: 10 }}>
            {camera === 'unsupported' && <span>This browser will not open the camera here. Type the code below.</span>}
            {camera === 'blocked' && <span>The camera is not available. Type the code below.</span>}
            <Btn primary big read onClick={() => setCamera('on')}>
              Use the camera
            </Btn>
          </div>
        )}
      </div>
      <form
        className="row mid"
        onSubmit={(e) => {
          e.preventDefault();
          setCode(typed.trim());
        }}
      >
        <input type="text" className="grow read" placeholder="Or type the code printed under it" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <Btn type="submit" read disabled={!typed.trim()}>
          Look up
        </Btn>
      </form>
      {code && <Result code={code} />}
    </div>
  );
}

/** Receiving a scanned material: against the order line it is coming on, or as a free receipt when nothing was ordered. */
function Receiving({ material, amount, setAmount, receiveFree }: { material: Row; amount: string; setAmount: (v: string) => void; receiveFree: () => void }) {
  const s = useSession();
  const inv = useInventory();
  const open = inv.orders.flatMap((o) => (o.po.kind === 'order' && o.state !== 'draft' && o.state !== 'closed' ? o.lines.filter((l) => l.row.material_id === material.id && Number(l.outstanding) > 0).map((l) => ({ o, l })) : []));
  const n = Number(amount);
  return (
    <div style={{ marginTop: 14 }}>
      <div className="lb">
        <span>Receive</span>
      </div>
      {open.map(({ o, l }) => (
        <Ln
          key={l.row.id}
          left={
            <>
              {o.po.number} · {o.vendor?.name ?? ''}
              <br />
              <small>
                {qty(l.outstanding)} {material.unit ?? ''} still to come
              </small>
            </>
          }
          right={
            <Btn
              big
              primary
              onClick={async () => {
                const got = n > 0 ? n : Number(l.outstanding);
                await receive(l, got, s.user?.id ?? null);
                setAmount('');
                toast(`${qty(got)} received against ${o.po.number}.`);
              }}
            >
              Receive {n > 0 ? qty(n) : qty(l.outstanding)}
            </Btn>
          }
        />
      ))}
      <div className="row mid" style={{ marginTop: 10, gap: 8 }}>
        <input type="number" className="short" placeholder="qty" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Btn big disabled={!(n > 0)} onClick={receiveFree}>
          {open.length > 0 ? 'Not from an order: free receipt' : 'Receive (bought in person)'}
        </Btn>
      </div>
      <small>{open.length > 0 ? 'Leave the quantity empty to receive everything still to come on that order, or type what arrived.' : 'Nothing is on order for this, so what arrives is recorded as a free receipt.'}</small>
    </div>
  );
}

/**
 * A kanban card was scanned. Two explicit actions, and the card's state says which applies:
 * the card was reached in its bin (raise the reorder), or its order has been put away
 * (the card goes back in the bin).
 */
function KanbanResult({ label, material, amount, setAmount, receiveFree }: { label: Row; material: Row; amount: string; setAmount: (v: string) => void; receiveFree: () => void }) {
  const s = useSession();
  const inv = useInventory();
  const names = useInventoryNames();
  const stateNames = useOrderStateNames();
  const [busy, setBusy] = useState(false);
  const bin = material.tracking_mode === 'bin';
  const raised = !!label.raised_line_id;
  const on = raised ? inv.orders.find((o) => o.lines.some((l) => l.requests.some((r) => r.id === label.raised_line_id))) : undefined;
  const run = async (fn: () => Promise<void>, done: string) => {
    setBusy(true);
    try {
      await fn();
      toast(done);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel label={`${cap(names.kanban_card)} ${label.card_no} · ${material.description}`}>
      <p>
        {bin ? 'Counted by the bin.' : `On hand: ${qty(material.stock_on_hand)} ${material.unit ?? ''}`}
        {material.order_quantity !== null && ` · ${names.order_quantity} ${qty(material.order_quantity)} ${material.unit ?? ''}`}
        {material.bin_location && ` · bin ${material.bin_location}`}
      </p>
      {!raised ? (
        <div style={{ marginTop: 10 }}>
          <Btn primary big disabled={busy} onClick={() => void run(() => kanbanRaise(label.id), 'Reorder raised. Put the card in the reorder slot.')}>
            Reached this card → raise the reorder
          </Btn>
          <div>
            <small>The card is in its bin. Raising the reorder puts it on the buy list; then put the card in the reorder slot.</small>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          <p>
            <b>Reorder raised {shortDate(label.raised_at)}</b> · {on ? `on ${on.po.number} (${stateNames[on.state].toLowerCase()})` : 'waiting to be ordered'}
          </p>
          <div className="row mid" style={{ marginTop: 8, gap: 8 }}>
            <Btn primary big disabled={busy} onClick={() => void run(() => kanbanReturn(label.id), 'Card is back in its bin.')}>
              Order put away → card back in its bin
            </Btn>
            {!on && (
              <Btn big disabled={busy} onClick={() => void run(() => kanbanReturn(label.id, true), 'Reorder cancelled. Card is back in its bin.')}>
                Scanned by mistake → cancel the reorder
              </Btn>
            )}
          </div>
        </div>
      )}
      {s.can('screen.receiving') && <Receiving material={material} amount={amount} setAmount={setAmount} receiveFree={receiveFree} />}
    </Panel>
  );
}

function Result({ code }: { code: string }) {
  const s = useSession();
  const label = useRows('label', { eq: { code } });
  const row = label.rows[0];
  const card = useRows('work_card', { eq: { id: row?.target_id }, enabled: row?.kind === 'card' }).rows[0];
  const material = useRows('material', { eq: { id: row?.target_id }, enabled: row?.kind === 'bin' || row?.kind === 'material' || row?.kind === 'kanban' }).rows[0];
  const item = useRows('job_item', { eq: { id: row?.target_id }, enabled: row?.kind === 'item' }).rows[0];
  const sop = useRows('sop', { eq: { id: row?.target_id }, enabled: row?.kind === 'sop' }).rows[0];
  const sopOperation = useRows('operation', { eq: { id: sop?.operation_id }, enabled: !!sop }).rows[0];
  const [amount, setAmount] = useState('');
  const names = useInventoryNames();

  if (label.loading) return <Panel>Looking it up…</Panel>;
  if (!row) return <Panel label={code}><Empty>No label carries this code.</Empty></Panel>;

  // every receipt is against an order line; with no order behind it, it is a free receipt
  const receiveFree = async (m: Row) => {
    const n = Number(amount);
    if (!(n > 0)) return;
    await freeReceipt(m.vendor_id ?? null, [{ material_id: m.id, qty: n, unit_price: null }], 'scanned in', s.user?.id ?? null);
    setAmount('');
    toast(`Received ${qty(n)} ${m.unit ?? ''} with no order behind it.`);
  };
  const reorder = async (m: Row) => {
    // a request still waiting to go on an order is not raised a second time
    const waiting = await fetchRows('purchase_line', { eq: { kind: 'reorder', material_id: m.id, purchase_order_line_id: null } });
    if (waiting.length > 0) {
      toast('A reorder line is already open for this.');
      return;
    }
    await insertRow('purchase_line', { kind: 'reorder', material_id: m.id, qty: Number(m.order_quantity ?? 0), note: 'bin scanned low' });
    toast('Reorder line added to purchasing.');
  };

  if (sop) {
    return (
      <Panel label={`SOP · ${sop.title}`}>
        <p>{sopOperation ? `For the operation: ${sopOperation.name}` : ''}</p>
        {sop.note && <p>{sop.note}</p>}
        <div style={{ marginTop: 10 }}>
          {sop.url || sop.file_id ? (
            <SopLink sop={sop}>
              <span className="btn p big">Open the SOP</span>
            </SopLink>
          ) : (
            <Empty>This SOP has no link or file yet.</Empty>
          )}
        </div>
      </Panel>
    );
  }
  if (card) {
    return (
      <Panel label={`Card #${card.number} · ${card.name}`}>
        <p>
          {card.state} · {Number(card.hours_standard).toFixed(1)} h standard
        </p>
        <div style={{ marginTop: 10 }}>
          <Link className="btn p big" to="/shop?view=tablet">
            Open the board
          </Link>
        </div>
      </Panel>
    );
  }
  if (material && row.kind === 'kanban') return <KanbanResult label={row} material={material} amount={amount} setAmount={setAmount} receiveFree={() => void receiveFree(material)} />;
  if (material) {
    const bin = material.tracking_mode === 'bin';
    return (
      <Panel label={`${bin ? 'Bin' : 'Material'} · ${material.description}`}>
        <p>
          {bin ? 'Counted by the bin.' : `On hand: ${qty(material.stock_on_hand)} ${material.unit ?? ''}`}
          {material.safety_stock !== null && ` · ${names.safety_stock} ${qty(material.safety_stock)}`}
        </p>
        <div className="row mid" style={{ marginTop: 10, gap: 8 }}>
          <Btn primary big onClick={() => void reorder(material)}>
            {bin ? 'Bin low → add reorder line' : 'Add reorder line'}
          </Btn>
        </div>
        {s.can('screen.receiving') && <Receiving material={material} amount={amount} setAmount={setAmount} receiveFree={() => void receiveFree(material)} />}
      </Panel>
    );
  }
  if (item) {
    return (
      <Panel label={`Item ${item.number} · ${item.name}`}>
        <Link className="btn p big" to={s.can('screen.jobs') ? `/jobs/${item.job_id}?item=${item.id}` : '/shop'}>
          Open it
        </Link>
      </Panel>
    );
  }
  return <Panel label={code}><Empty>The thing this label points at no longer exists.</Empty></Panel>;
}
